import { i18n } from '@/locales'
import axios from 'axios'

const BASEURL = process.env.VUE_APP_APIA_URL
const IDEAS = 'iideas/v1/ideas'

const minute = 1000 * 60
const day = minute * 60 * 24

const pageSize = 18

const ideas = {
  namespaced: true,
  state: {
    canBeLoaded: false,
    pages: 0,
    list: [],
    idea: {},
    filters: {},
  },
  mutations: {
    setCanBeLoaded(state, payload) {
      state.canBeLoaded = payload
    },
    setPages(state, payload) {
      state.pages = payload
    },
    setIdeasList(state, payload) {
      state.list = payload
    },
    setMoreIdeasList(state, payload) {
      state.list.push(...payload)
    },
    setFullIdea(state, payload) {
      state.idea = payload
    },
    pushFilter(state, payload) {
      if (
        state.filters[payload.filter.queryParam] === payload.param.queryParam ||
        (payload.filter.queryParam === 'ticker_contains' &&
          payload.param.queryParam.length < 2)
      ) {
        delete state.filters[payload.filter.queryParam]
      } else {
        state.filters[payload.filter.queryParam] = payload.param.queryParam
      }
    },
    setFilters(state, payload) {
      state.filters = payload
    },
  },
  actions: {
    activateFilters({ commit, getters }, payload) {
      let newFilters = {}
      for (let param in payload) {
        if (param === 'ticker_contains') {
          if (payload[param]) {
            newFilters[param] = payload[param]
          }
        } else {
          // for each param
          getters.getFilters.find((filter) => {
            // find filter
            if (filter.queryParam === param) {
              // and find value of filter
              const foundedParam = filter.values.find(
                (el) => el.queryParam === payload[param]
              )
              // if find filter and the value, activate this value
              if (foundedParam) {
                newFilters[param] = payload[param]
              }
              // if true, find() will close
              return filter.queryParam === param
            }
          })
        }
      }
      commit('setFilters', newFilters)
    },
    loadIdeasList({ state, commit, rootState }) {
      let obj = {
        params: {
          start: state.pages * pageSize,
          limit: pageSize,
          ...state.filters,
        },
      }
      if (rootState.request.authorized.access) {
        obj.headers = {
          Authorization: `Bearer ${rootState.request.authorized.access}`,
        }
      }
      return new Promise((resolve, reject) => {
        axios
          .get(`${BASEURL}${IDEAS}`, obj)
          .then((res) => {
            commit(state.pages ? 'setMoreIdeasList' : 'setIdeasList', res.data)
            commit('setPages', state.pages + 1)
            commit('setCanBeLoaded', res.data.length === pageSize)
            resolve()
          })
          .catch((err) => {
            // dispatch layout/showError
            reject(err)
          })
      })
    },
    loadFullIdea({ commit, rootState }, { id }) {
      let obj = {}
      if (rootState.request.authorized.access) {
        obj.headers = {
          Authorization: `Bearer ${rootState.request.authorized.access}`,
        }
      }
      return new Promise((resolve, reject) => {
        axios
          .get(`${BASEURL}${IDEAS}/${id}`, obj)
          .then((res) => {
            commit('setFullIdea', res.data)
            resolve()
          })
          .catch((err) => {
            // dispatch layout/showError
            reject(err)
          })
      })
    },
  },
  getters: {
    getFilters: () => {
      return [
        {
          values: [
            {
              name: i18n.t(`ideas.filters.filter.running.title`),
              queryParam: '1',
            },
          ],
          queryParam: 'running',
          type: 'checkbox',
        },
        {
          title: i18n.t(`ideas.filters.filter.target_pnl.title`),
          values: [
            { name: '< 10%', queryParam: 'lt10' },
            { name: '10%-50%', queryParam: '10_50' },
            { name: '> 50%', queryParam: 'gt50' },
          ],
          queryParam: 'target_pnl',
          type: 'radio',
        },
        {
          title: i18n.t(`ideas.filters.filter.idea_opened_gt.title`),
          values: [
            {
              name: i18n.t(`ideas.filters.filter.idea_opened_gt.3`),
              queryParam: '3',
            },
            {
              name: i18n.t(`ideas.filters.filter.idea_opened_gt.7`),
              queryParam: '7',
            },
            {
              name: i18n.t(`ideas.filters.filter.idea_opened_gt.30`),
              queryParam: '30',
            },
            {
              name: i18n.t(`ideas.filters.filter.idea_opened_gt.90`),
              queryParam: '90',
            },
          ],
          queryParam: 'idea_opened_gt',
          type: 'radio',
        },
        {
          title: i18n.t(`ideas.filters.filter.current_pnl.title`),
          values: [
            {
              name: i18n.t(`ideas.filters.filter.current_pnl.gte0`),
              queryParam: 'gte0',
            },
            {
              name: i18n.t(`ideas.filters.filter.current_pnl.lte0`),
              queryParam: 'lte0',
            },
          ],
          queryParam: 'current_pnl',
          type: 'radio',
        },
      ]
    },
    getFullIdea: (state, getters) => {
      let idea = state.idea
      idea.period = getters.getIdeaPeriodAndTimeLeft(state.idea)
      return idea
    },
    getIdeasList: (state, getters) => {
      let list = state.list
      list.forEach((idea) => {
        idea.period = getters.getIdeaPeriodAndTimeLeft(idea)
      })
      return list
    },
    getIdeaPeriodAndTimeLeft: (state, getters) => (idea) => {
      const ideaPeriod = getters.getIdeaPeriod(idea.openDate, idea.targetDate)
      const timeLeft = getters.getTimeLeft(+new Date(), idea.targetDate)
      return ideaPeriod + ' (' + timeLeft + ')'
    },
    getIdeaPeriod: (state, getters) => (open, close) => {
      let period = getters.calcPeriod(open, close)
      return period
        ? i18n.tc(`ideas.period.${period.name}`, period.number, {
            count: period.number,
          })
        : ''
    },
    getTimeLeft: (state, getters) => (open, close) => {
      let remaining = ''
      let period = getters.calcPeriod(open, close)
      if (period.name === 'minute' || period.name === 'week') {
        remaining = i18n.tc(`ideas.period.remain`, period.number === 1 ? 2 : 1)
      } else {
        remaining = i18n.tc(`ideas.period.remain`, period.number === 1 ? 0 : 1)
      }
      remaining += ' '
      return period
        ? remaining +
            i18n.tc(`ideas.period.${period.name}`, period.number, {
              count: period.number,
            })
        : i18n.t(`ideas.period.closed`)
    },
    // TODO: describe what the function does with JSDock
    calcPeriod: () => (open, close) => {
      let diff = Math.round(close - open)
      if (diff < 0) return ''
      let period = {}
      let periodNames = { hour: 60, minute: 1 }
      if (diff >= day) {
        // calculate idea by days
        diff /= day
        periodNames = { year: 365, month: 30, week: 7, day: 1 }
      } else {
        // calculate idea by minutes
        diff /= minute
      }
      for (let periodName in periodNames) {
        if (diff >= periodNames[periodName]) {
          // number of minutes/days
          period.number = Math.round(diff / periodNames[periodName])
          period.name = periodName
          return period
        }
      }
      // returns if didn't find
      return ''
    },
  },
}

export default ideas
